import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiFileTextLight,
} from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import BrandLoader from '../shared/brand/BrandLoader';
import Alert from '../shared/TailwindComponents/Alert';
import FileIcon from '../shared/attachments/fileIcon';
import {
  ExtractionFileProgress,
  ExtractionRun,
  ExtractionRunStep,
  FailedDocument,
} from './playbookExtraction';

/**
 * "Generating playbook…" - the live view of an extraction run.
 *
 * Generation reads N documents through a pipeline whose length the engine
 * decides at runtime, so this panel shows what it is told rather than a
 * pre-drawn four-step checklist: one overall bar, the steps as they arrive,
 * and a sub-progress row per file underneath.
 */

// ─── Bar ─────────────────────────────────────────────────────────────────────

const ProgressBar: React.FC<{
  percent: number | null;
  tone?: 'champagne' | 'muted';
  className?: string;
}> = ({ percent, tone = 'champagne', className }) => (
  <div
    className={clsx('h-1.5 rounded-full bg-surface-sunken overflow-hidden', className)}
    role="progressbar"
    aria-valuenow={percent ?? undefined}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className={clsx(
        'h-full rounded-full transition-[width] duration-500 ease-slide',
        tone === 'champagne' ? 'bg-champagne' : 'bg-fg-muted/40',
        // No number yet: a moving sliver reads as "working", a full bar lies.
        percent === null && 'w-1/3 animate-pulse',
      )}
      style={percent === null ? undefined : { width: `${percent}%` }}
    />
  </div>
);

// ─── Step row ────────────────────────────────────────────────────────────────

const StepRow: React.FC<{ step: ExtractionRunStep }> = ({ step }) => (
  <li className="flex items-start gap-2 text-xs">
    <span className="mt-0.5 shrink-0">
      {step.status === 'completed' ? (
        <PiCheckCircleLight className="w-4 h-4 text-success-500" />
      ) : step.status === 'failed' ? (
        <PiWarningCircleLight className="w-4 h-4 text-danger-500" />
      ) : step.status === 'in_progress' ? (
        <BrandLoader size="xs" className="!h-4 !w-4" />
      ) : (
        <span className="block w-4 h-4 rounded-full border border-hairline" />
      )}
    </span>
    <span
      className={clsx(
        'min-w-0 break-words',
        step.status === 'completed'
          ? 'text-fg-muted'
          : step.status === 'failed'
            ? 'text-danger-600 dark:text-danger-400'
            : step.status === 'in_progress'
              ? 'text-fg-strong'
              : 'text-fg-muted',
      )}
    >
      {step.name}
      {step.message && step.message !== step.name && (
        <span className="block text-[11px] text-fg-muted">{step.message}</span>
      )}
      {/* The engine's aside, one notch quieter than the message it explains. */}
      {step.narration && step.narration !== step.message && (
        <span className="block text-[11px] text-fg-muted/70">{step.narration}</span>
      )}
    </span>
  </li>
);

// ─── File row ────────────────────────────────────────────────────────────────

const FileRow: React.FC<{ file: ExtractionFileProgress }> = ({ file }) => {
  const { t } = useTranslation();
  return (
    <li className="rounded-md bg-surface-sunken/70 border border-hairline-soft px-2.5 py-2">
      <div className="flex items-center gap-2">
        <FileIcon name={file.name} className="h-4 w-4 shrink-0" />
        <span className="flex-1 min-w-0 text-xs text-fg-body truncate" title={file.name}>
          {file.name}
        </span>
        {file.status === 'failed' ? (
          <PiWarningCircleLight className="w-4 h-4 text-danger-500 shrink-0" />
        ) : file.status === 'completed' ? (
          <PiCheckCircleLight className="w-4 h-4 text-success-500 shrink-0" />
        ) : (
          <span className="text-[11px] text-fg-muted shrink-0 tabular-nums">
            {file.percent === null ? t('Waiting') : `${file.percent}%`}
          </span>
        )}
      </div>
      {file.status !== 'completed' && file.status !== 'failed' && (
        <ProgressBar
          percent={file.status === 'pending' ? 0 : file.percent}
          tone={file.status === 'pending' ? 'muted' : 'champagne'}
          className="mt-1.5 h-1"
        />
      )}
      {file.message && (
        <p className="mt-1 text-[11px] text-fg-muted break-words">{file.message}</p>
      )}
    </li>
  );
};

// ─── Failed documents ────────────────────────────────────────────────────────

export interface PlaybookFailedDocumentsProps {
  documents: FailedDocument[];
  onDismiss?: () => void;
  className?: string;
}

/**
 * The quiet half of a good run: the engine finished and wrote rules, but some
 * files were unreadable. Previously these vanished silently, so a playbook
 * built from nine of ten documents looked identical to one built from ten.
 */
export const PlaybookFailedDocuments: React.FC<PlaybookFailedDocumentsProps> = ({
  documents,
  onDismiss,
  className,
}) => {
  const { t } = useTranslation();
  if (!documents.length) return null;

  return (
    <div
      className={clsx(
        // The Alert is sized for a toast; here it sits in a column. Its second
        // line carries one filename per row, so newlines have to survive.
        '[&_.alert]:min-w-0 [&_.alert]:max-w-none [&_.alert_p+p]:whitespace-pre-line',
        className,
      )}
    >
      <Alert
        type="warning"
        message={
          documents.length === 1
            ? t('One document could not be read')
            : t('{{count}} documents could not be read', { count: documents.length })
        }
        subMessage={documents
          .map((d) => (d.reason ? `${d.name} - ${d.reason}` : d.name))
          .join('\n')}
        onClose={onDismiss}
      />
    </div>
  );
};

// ─── Panel ───────────────────────────────────────────────────────────────────

export interface PlaybookExtractionProgressProps {
  run: ExtractionRun;
  /** Header line while running. Defaults to "Generating playbook…". */
  title?: string;
  /**
   * Header lines for the two finished states. Overridable because the same
   * panel covers augmenting an existing playbook, where "Playbook generated"
   * would misdescribe what happened - nothing was regenerated, rules were
   * merged in.
   */
  completedTitle?: string;
  failedTitle?: string;
  onDismissFailedDocuments?: () => void;
  /**
   * `centered` gives the panel the whole content area and floats it in the
   * middle of it, at a reading width. Generation is the only thing on screen
   * while it runs, so a card hugging the top-left corner of an empty canvas
   * read as a stray notification rather than the work in progress.
   */
  layout?: 'inline' | 'centered';
  /** Controls that belong WITH the card - e.g. "Continue in background". */
  footer?: React.ReactNode;
  className?: string;
}

const PlaybookExtractionProgress: React.FC<PlaybookExtractionProgressProps> = ({
  run,
  title,
  completedTitle,
  failedTitle,
  onDismissFailedDocuments,
  layout = 'inline',
  footer,
  className,
}) => {
  const { t } = useTranslation();
  const isFinished = run.status === 'completed' || run.status === 'failed';
  const centered = layout === 'centered';
  const showPercent = run.status !== 'connecting' && run.status !== 'failed';

  const panel = (
    <div className={clsx('space-y-3', centered ? 'w-full max-w-xl' : undefined, !centered && className)}>
      <GlassCard
        padding="md"
        tone={run.status === 'failed' ? 'danger' : run.status === 'completed' ? 'success' : 'info'}
        className="space-y-4"
      >
        {/* Headline + overall bar */}
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-surface-sunken/70 flex items-center justify-center shrink-0">
            {run.status === 'failed' ? (
              <PiWarningCircleLight className="w-5 h-5 text-danger-500" />
            ) : run.status === 'completed' ? (
              <PiCheckCircleLight className="w-5 h-5 text-success-500" />
            ) : (
              <BrandLoader size="xs" className="!h-5 !w-5" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-fg-strong">
              {run.status === 'failed'
                ? failedTitle || t('Playbook generation failed')
                : run.status === 'completed'
                  ? completedTitle || t('Playbook generated')
                  : title || t('Generating playbook…')}
            </p>
            {/* Primary line: whatever the engine last said it was doing. */}
            <p className="text-xs text-fg-muted break-words">
              {run.error ||
                run.message ||
                (run.status === 'connecting'
                  ? t('Connecting to the processing pipeline…')
                  : t('Editing is disabled while generation is in progress'))}
            </p>
            {/* Secondary line: the engine's optional narration, de-emphasized
                the same way the research surface treats its interim lines. */}
            {!isFinished && run.narration && run.narration !== run.message && (
              <p className="mt-0.5 text-[11px] text-fg-muted/70 break-words">{run.narration}</p>
            )}
          </div>
          {/* Kept on screen through completion: the bar reaching 100 is the
              confirmation that the run finished, and it was previously
              replaced by nothing at the exact moment it mattered. */}
          {showPercent && (
            <span className="text-xs text-fg-muted tabular-nums shrink-0">{run.percent}%</span>
          )}
        </div>

        {run.status !== 'failed' && (
          <ProgressBar percent={run.status === 'connecting' ? null : run.percent} />
        )}

        {/* Steps - however many the engine reports */}
        {run.steps.length > 0 && (
          <div className="space-y-2">
            {run.stepTotal > 0 && (
              <p className="text-[11px] uppercase tracking-wide text-fg-muted">
                {/* Both numbers come from the engine - the pipeline decides its
                    own length, so nothing here is pre-drawn. */}
                {t('Step {{current}} of {{total}}', {
                  current: Math.min(
                    run.stepTotal,
                    Math.max(
                      run.stepCurrent,
                      run.steps.filter((s) => s.status !== 'pending').length,
                    ),
                  ),
                  total: run.stepTotal,
                })}
              </p>
            )}
            <ul className="space-y-2">
              {run.steps.map((step) => (
                <StepRow key={step.id} step={step} />
              ))}
            </ul>
          </div>
        )}

        {/* Per-file sub-progress */}
        {run.files.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-fg-muted flex items-center gap-1.5">
              <PiFileTextLight className="w-3.5 h-3.5" />
              {run.files.length === 1
                ? t('1 document')
                : t('{{count}} documents', { count: run.files.length })}
            </p>
            <ul className="space-y-1.5">
              {run.files.map((file) => (
                <FileRow key={file.name} file={file} />
              ))}
            </ul>
          </div>
        )}
      </GlassCard>

      <PlaybookFailedDocuments
        documents={run.failedDocuments}
        onDismiss={onDismissFailedDocuments}
      />

      {footer && (
        <div className={clsx('flex gap-2', centered ? 'justify-center' : 'justify-end')}>
          {footer}
        </div>
      )}
    </div>
  );

  if (!centered) return panel;

  return (
    <div
      className={clsx(
        // Fills whatever the parent gives it and floats the card in the middle
        // of that. `min-h` covers the case where the parent has no height of
        // its own, so the card is never pinned to the top of a tall empty area.
        'w-full flex-1 flex flex-col items-center justify-center px-4 py-10 min-h-[60vh]',
        className,
      )}
    >
      {panel}
    </div>
  );
};

export default PlaybookExtractionProgress;
